export type RGB = [number, number, number];

export function hexToRgb(h: string): RGB {
  let s = String(h || '#000').replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  const n = parseInt(s, 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export const rgbToHex = (a: number[]) =>
  '#' + a.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
export const mix = (a: string, b: string, t: number) => {
  const A = hexToRgb(a), B = hexToRgb(b);
  return rgbToHex(A.map((v, i) => v + (B[i] - v) * t));
};
export function lum(h: string) {
  const [r, g, b] = hexToRgb(h).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** Black or white, whichever reads better on the colour. */
export const onColor = (h: string) => {
  const L = lum(h);
  return (L + 0.05) / 0.0603 >= 1.05 / (L + 0.05) ? '#1A1A1A' : '#FFFFFF';
};
export const isDark = (h: string) => lum(h) < 0.18;
export const isHex = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v);

export function sat0(h: string) {
  const [r, g, b] = hexToRgb(h);
  const mx = Math.max(r, g, b);
  return mx ? (mx - Math.min(r, g, b)) / mx : 0;
}

export function rgbToHsl([r0, g0, b0]: RGB): RGB {
  const r = r0 / 255, g = g0 / 255, b = b0 / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
  let h = 0, s = 0;
  if (mx !== mn) {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return [h, s, l];
}
export function hslToHex(h0: number, s: number, l: number) {
  const h = ((h0 % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return rgbToHex([r, g, b].map((v) => (v + m) * 255));
}
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
/** Two companion colours for the Confetti design, derived from the accent. */
export function extraColours(accent: string) {
  const [h, s, l] = rgbToHsl(hexToRgb(accent));
  if (s < 0.15) return { c2: '#3A8D8A', c3: '#E9B44C' };
  return {
    c2: hslToHex(h + 165, clamp(s, 0.35, 0.55), clamp(l, 0.34, 0.44)),
    c3: hslToHex(h + 30, clamp(s, 0.65, 0.8), clamp(l + 0.1, 0.58, 0.64)),
  };
}
